import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Calendar, ArrowLeft, Plus, Edit2, Trash2, Menu, X, ChevronRight, ChevronLeft, Mail } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import ImageCarousel from '../components/ImageCarousel';

const Blog = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const logoSrc = '/logo.png';

  // Admin email - only this user can create analysis examples
  const ADMIN_EMAIL = (process.env.REACT_APP_ADMIN_EMAIL || '').trim().toLowerCase();
  const isAdmin = user && ADMIN_EMAIL && user.email?.toLowerCase() === ADMIN_EMAIL;

  const [selectedEntry, setSelectedEntry] = useState(null);
  const [entries, setEntries] = useState([]);
  const [categories, setCategories] = useState({ Equities: [], FX: [], Rates: [], Commodities: [] });
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [emailStatus, setEmailStatus] = useState({ loading: false, message: '' });

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('blog_entries')
      .select('*')
      .order('example_number', { ascending: true });

    if (error) {
      console.error('Error fetching entries:', error);
      setEntries([]);
      setSelectedEntry(null);
      setLoading(false);
      return;
    }

    setEntries(data);

    // Group entries by category
    const grouped = {
      Equities: [],
      FX: [],
      Rates: [],
      Commodities: []
    };

    data.forEach(entry => {
      const category = entry.category || 'Equities'; // Default to Equities if no category
      if (grouped[category]) {
        grouped[category].push(entry);
      }
    });

    setCategories(grouped);

    // Check URL parameter for specific entry
    const exampleNumber = searchParams.get('example');
    if (exampleNumber && data.length > 0) {
      const urlEntry = data.find(entry => entry.example_number === parseInt(exampleNumber));
      if (urlEntry) {
        setSelectedEntry(urlEntry);
        setSelectedCategory(urlEntry.category || 'Equities');
        setLoading(false);
        return;
      }
    }

    // Select first non-empty category and first entry
    const firstCategory = Object.keys(grouped).find(cat => grouped[cat].length > 0);
    if (firstCategory) {
      setSelectedCategory(firstCategory);
      setSelectedEntry((prev) => {
        if (!prev) return grouped[firstCategory][0];
        const stillExists = data.find((entry) => entry.id === prev.id);
        return stillExists || grouped[firstCategory][0];
      });
    } else {
      setSelectedEntry(null);
      setSelectedCategory(null);
    }

    setLoading(false);
  }, [searchParams]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this entry? This action cannot be undone.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('blog_entries')
        .delete()
        .eq('id', entryId);

      if (error) throw error;

      await fetchEntries();
    } catch (error) {
      console.error('Error deleting entry:', error);
      alert('Failed to delete entry. Please try again.');
    }
  };

  const handleEdit = (entryId) => {
    navigate(`/blog/edit/${entryId}`);
  };

  const handleEntrySelect = (entry) => {
    setSelectedEntry(entry);
    setMobileMenuOpen(false);
    // Update URL with example number
    setSearchParams({ example: entry.example_number });
  };

  // Navigate to previous/next example within the full list
  const handlePreviousExample = () => {
    if (!selectedEntry) return;
    const currentIndex = entries.findIndex((entry) => entry.id === selectedEntry.id);
    if (currentIndex > 0) {
      const prevEntry = entries[currentIndex - 1];
      setSelectedEntry(prevEntry);
      setSearchParams({ example: prevEntry.example_number });
    }
  };

  const handleNextExample = () => {
    if (!selectedEntry) return;
    const currentIndex = entries.findIndex((entry) => entry.id === selectedEntry.id);
    if (currentIndex < entries.length - 1) {
      const nextEntry = entries[currentIndex + 1];
      setSelectedEntry(nextEntry);
      setSearchParams({ example: nextEntry.example_number });
    }
  };

  // Get current position info
  const getCurrentExampleInfo = () => {
    if (!selectedEntry || entries.length === 0) return { current: 0, total: entries.length };
    const currentIndex = entries.findIndex((entry) => entry.id === selectedEntry.id);
    return { current: currentIndex + 1, total: entries.length };
  };

  // Email entry to support
  const handleEmailEntry = async () => {
    if (!selectedEntry) return;

    setEmailStatus({ loading: true, message: '' });

    try {
      const response = await fetch('http://localhost:3001/api/email-entry', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          entry: selectedEntry,
          type: 'blog'
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setEmailStatus({
          loading: false,
          message: data.skipped ? 'Email not configured' : 'Email sent successfully!'
        });
        setTimeout(() => setEmailStatus({ loading: false, message: '' }), 3000);
      } else {
        setEmailStatus({ loading: false, message: 'Failed to send email' });
        setTimeout(() => setEmailStatus({ loading: false, message: '' }), 3000);
      }
    } catch (error) {
      console.error('Error sending email:', error);
      setEmailStatus({ loading: false, message: 'Error sending email' });
      setTimeout(() => setEmailStatus({ loading: false, message: '' }), 3000);
    }
  };

  return (
    <div className="min-h-screen bg-[#111315] text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#111315]/95 backdrop-blur-sm border-b border-neutral-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3 group">
              <img
                src={logoSrc}
                alt="HDAlgos"
                className="h-10 w-auto"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <span className="text-2xl font-black tracking-tight">
                <span className="text-white">HD</span>
                <span className="text-[#CBB26A]">Algos</span>
              </span>
            </Link>

            <div className="flex items-center gap-4">
              {isAdmin && (
                <Link
                  to="/blog/admin"
                  className="flex items-center gap-2 px-4 py-2 bg-[#CBB26A] text-black font-bold rounded-lg hover:bg-[#A8914D] transition"
                >
                  <Plus className="w-5 h-5" />
                  <span>New Entry</span>
                </Link>
              )}

              <Link
                to="/"
                className="flex items-center gap-2 text-neutral-400 hover:text-white transition"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="hidden sm:inline">Back to Home</span>
              </Link>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 hover:bg-neutral-800 rounded-lg transition"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="flex pt-20">
        {/* Mobile overlay */}
        {mobileMenuOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-30 lg:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}

        {/* Sidebar */}
        <aside
          className={`
            fixed lg:sticky top-20 left-0 h-[calc(100vh-5rem)] w-80 bg-[#1a1d21] border-r border-neutral-800
            overflow-y-auto z-40 transition-transform duration-300 ease-in-out
            ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          `}
        >
          <div className="p-6">
            <div className="flex items-center gap-2 mb-6">
              <Calendar className="w-5 h-5 text-[#CBB26A]" />
              <h2 className="text-lg font-bold">Trading Journal</h2>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#CBB26A]"></div>
              </div>
            ) : entries.length === 0 ? (
              <p className="text-neutral-500 text-sm">No analysis examples yet.</p>
            ) : (
              <div className="space-y-2">
                {Object.keys(categories).map((category) => (
                  <div key={category}>
                    {/* Category Header */}
                    <button
                      onClick={() => {
                        setSelectedCategory(selectedCategory === category ? null : category);
                        if (selectedCategory !== category && categories[category].length > 0) {
                          setSelectedEntry(categories[category][0]);
                          setMobileMenuOpen(false);
                        }
                      }}
                      className={`
                        w-full text-left px-4 py-3 rounded-lg transition flex items-center justify-between
                        ${selectedCategory === category
                          ? 'bg-[#CBB26A]/10 text-[#CBB26A]'
                          : 'hover:bg-neutral-800 text-neutral-300'
                        }
                      `}
                    >
                      <span className="font-medium">{category}</span>
                      <div className="flex items-center gap-2">
                        {categories[category].length > 0 && (
                          <span className="text-xs bg-neutral-800 px-2 py-1 rounded">
                            {categories[category].length}
                          </span>
                        )}
                        <ChevronRight
                          className={`w-4 h-4 transition-transform ${
                            selectedCategory === category ? 'rotate-90' : ''
                          }`}
                        />
                      </div>
                    </button>

                    {/* Examples under this category */}
                    {selectedCategory === category && categories[category] && (
                      <div className="ml-4 mt-2 space-y-1">
                        {categories[category].map((entry) => (
                          <button
                            key={entry.id}
                            onClick={() => handleEntrySelect(entry)}
                            className={`
                              w-full text-left px-4 py-2 rounded-lg transition text-sm
                              ${selectedEntry && selectedEntry.id === entry.id
                                ? 'bg-[#CBB26A] text-black font-medium'
                                : 'hover:bg-neutral-800 text-neutral-400'
                              }
                            `}
                          >
                            Example {entry.example_number}: {entry.title}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-12">
          <div className="max-w-4xl mx-auto">
            {!loading && entries.length > 0 && (
              <div className="hidden lg:flex items-center gap-3 text-[#CBB26A] font-semibold mb-6">
                <ArrowLeft className="w-5 h-5" />
                <span>More examples</span>
              </div>
            )}
            {loading ? (
              <div className="flex items-center justify-center py-24">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#CBB26A]"></div>
              </div>
            ) : !selectedEntry ? (
              <div className="text-center py-24">
                <Calendar className="w-16 h-16 text-neutral-600 mx-auto mb-4" />
                <h2 className="text-2xl font-bold mb-2">No Entry Selected</h2>
                <p className="text-neutral-400">
                  {entries.length === 0
                    ? 'No analysis examples yet. Create your first entry!'
                    : 'Select an example from the sidebar to view.'}
                </p>
                <p className="text-neutral-500 text-sm mt-2 lg:hidden">
                  Tap the menu icon to open the sidebar
                </p>
              </div>
            ) : (
              <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-8 relative">
                {/* Navigation Arrows */}
                {(() => {
                  const { current, total } = getCurrentExampleInfo();
                  return total > 1 && (
                    <div className="flex items-center justify-between mb-6 pb-4 border-b border-neutral-800">
                      <button
                        onClick={handlePreviousExample}
                        disabled={current === 1}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${
                          current === 1
                            ? 'opacity-30 cursor-not-allowed'
                            : 'hover:bg-neutral-800 text-neutral-300 hover:text-white'
                        }`}
                      >
                        <ChevronLeft className="w-5 h-5" />
                        <span className="hidden sm:inline">Previous</span>
                      </button>

                      <div className="text-sm text-neutral-400 font-medium">
                        Example {current} of {total}
                      </div>

                      <button
                        onClick={handleNextExample}
                        disabled={current === total}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${
                          current === total
                            ? 'opacity-30 cursor-not-allowed'
                            : 'hover:bg-neutral-800 text-neutral-300 hover:text-white'
                        }`}
                      >
                        <span className="hidden sm:inline">Next</span>
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    </div>
                  );
                })()}

                {/* Admin Controls */}
                {isAdmin && (
                  <div className="absolute top-4 right-4 flex gap-2">
                    <button
                      onClick={handleEmailEntry}
                      disabled={emailStatus.loading}
                      className="p-2 bg-blue-900/50 hover:bg-blue-900 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Email to support@hdalgos.com"
                    >
                      <Mail className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleEdit(selectedEntry.id)}
                      className="p-2 bg-neutral-700 hover:bg-neutral-600 rounded-lg transition"
                      title="Edit entry"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(selectedEntry.id)}
                      className="p-2 bg-red-900/50 hover:bg-red-900 rounded-lg transition"
                      title="Delete entry"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* Email Status Message */}
                {emailStatus.message && (
                  <div className={`mb-4 p-3 rounded-lg ${
                    emailStatus.message.includes('successfully')
                      ? 'bg-green-900/50 border border-green-800 text-green-300'
                      : 'bg-red-900/50 border border-red-800 text-red-300'
                  }`}>
                    {emailStatus.message}
                  </div>
                )}

                {/* Entry Header */}
                <div className="mb-6">
                  <div className="text-sm text-neutral-500 mb-2">
                    {formatDate(selectedEntry.date)}
                  </div>
                  <div className="text-sm text-[#CBB26A] font-bold mb-2">
                    EXAMPLE {selectedEntry.example_number}
                  </div>
                  <h1 className="text-3xl font-bold text-white">
                    {selectedEntry.title}
                  </h1>
                </div>

                {/* Images */}
                <ImageCarousel
                  images={selectedEntry.image_urls || (selectedEntry.image_url ? [selectedEntry.image_url] : [])}
                />

                {/* Content */}
                <div className="prose prose-invert prose-lg max-w-none">
                  <p className="text-neutral-300 leading-relaxed whitespace-pre-wrap">
                    {selectedEntry.content}
                  </p>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Blog;
